import { Modal } from 'antd'
import FormLabel from 'components/FormLabel'
import React, { useEffect, useState } from 'react'
import { isDesktop, isMobile, isTablet } from 'react-device-detect'
import CloseIcon from '../../assets/close-icon.svg'
import WarningIcon from '../../assets/icons/warning-icon.svg'
import { formatNumber, isAmtDivisibleBySharePrice } from '../../utils'
import Button from '../Button'
import ErrorMessage from '../ErrorMessage'
import InputField from '../InputField'

interface NonAccreditedInvestorModalProps {
  isVisible: boolean
  onDismiss: () => void
  getValues: any
  maxInvestmentAmount: number
  registerField: any
  control: any
  errors: any
  setValue: any
  nextStep: any
  handleSubmit: any
  isValid: boolean
  validateFields: any
  clearErrors: any
  isMaxInvestmentValid: boolean
}

const NonAccreditedInvestorModal = ({
  isVisible,
  onDismiss,
  getValues,
  maxInvestmentAmount,
  registerField,
  control,
  errors,
  setValue,
  handleSubmit,
  nextStep,
  isValid,
  validateFields,
  clearErrors,
  isMaxInvestmentValid,
}: NonAccreditedInvestorModalProps) => {
  const sharePrice = Number(process.env.REACT_APP_SHARE_PRICE)
  const minInvestment = Number(process.env.REACT_APP_MIN_INVESTMENT)

  const [selected, setSelected] = useState(false)

  const handleSelection = (selected: boolean) => {
    if (selected) {
      setSelected(false)
    } else {
      setSelected(true)
    }
  }

  const handleSave = async () => {
    await validateFields(['temp.investmentAmount'])

    if (isValid) {
      setValue('investingProfile.amount', Number(getValues('temp.investmentAmount').replace(/[$,]/g, '')))
      setValue(
        'investingProfile.totalShares',
        Number(getValues('temp.investmentAmount').replace(/[$,]/g, '')) / Number(sharePrice),
      )
      nextStep('7')
    }
  }

  useEffect(() => {
    setValue('temp.investmentAmount', String(maxInvestmentAmount))
  }, [maxInvestmentAmount, setValue])

  return (
    <>
      <Modal
        width={`${(isMobile || isTablet) && !isDesktop ? '90vw' : '50vw'}`}
        open={isVisible}
        centered
        footer={null}
        closable={false}
      >
        <div className="modal-content flex justify-end p-4">
          <img className="h-6 w-6" src={CloseIcon} alt="Close" onClick={onDismiss} />
        </div>

        <div className="flex flex-col items-center pb-5 md:px-10">
          <div className="w-full text-left font-bold md:text-6xl">
            <img src={WarningIcon} alt={`${WarningIcon}`} className="max-w-[52px]" />
          </div>
          <div className="w-full pb-2 pt-4 text-left text-2xl font-semibold text-gray-900 md:pb-8 md:text-4xl ">
            {isMaxInvestmentValid ? 'Investment amount warning' : 'Investment amount note'}
          </div>
          {isMaxInvestmentValid ? (
            <>
              <div className="mt-4 w-full text-left text-base font-light md:text-lg md:leading-normal">
                Due to your annual income, your net worth (or both), we need to adjust your investment amount.
              </div>
              <div className="mt-4 text-left text-base md:text-lg md:leading-normal">
                Your investment amount has been adjusted to the maximum amount you may legally invest. You may also also
                enter a different amount below.
              </div>
            </>
          ) : (
            <>
              <div className="mt-4 text-left text-base md:text-lg md:leading-normal">
                Due to your annual income, your net worth (or both) you are not allowed by law to invest the minimum
                amount of $200 in this offering. Please review your net worth and annual income and confirm that you put
                in the correct information.
              </div>
              <div className="mt-4 text-left text-base md:text-lg md:leading-normal">
                Note that if you are not an accredited investor, you are limited in how much you can invest to no more
                than 10% of the greater of your annual income or net worth. If the information you provided is correct,
                unfortunately you are not able to invest at this time.
              </div>
            </>
          )}

          {isMaxInvestmentValid && (
            <div className="mt-4 w-full text-3xl font-bold">
              <FormLabel labelName={'Investment Amount'} />
              <InputField
                registerField={registerField}
                fieldId={'temp.investmentAmount'}
                validate={{
                  numbersOnly: (value: any) => /^[0-9.$,]+$/.test(value),
                  minimumAmount: (value: any) => {
                    return Number(String(value).replace(/[$,]/g, '')) >= minInvestment
                  },
                  isDivisible: (value: any) => isAmtDivisibleBySharePrice(value, sharePrice),
                  maximumAmount: (value: any) => Number(String(value).replace(/[$,]/g, '')) <= maxInvestmentAmount,
                }}
                maskedInput={'numeric'}
                control={control}
                prefix={'$'}
                thousandsGroupStyle={'thousand'}
                thousandSeparator={true}
                decimalScale={2}
                maskType={'numeric'}
                value={String(maxInvestmentAmount)}
                onChange={() => clearErrors('temp.investmentAmount')}
              />
              {errors.temp?.investmentAmount?.type === 'numbersOnly' && (
                <ErrorMessage message={'Must input a valid amount.'} />
              )}
              {errors.temp?.investmentAmount?.type === 'minimumAmount' && (
                <ErrorMessage message={`Minimum investment is $${minInvestment}.`} />
              )}
              {errors.temp?.investmentAmount?.type === 'isDivisible' && (
                <ErrorMessage
                  message={`Your investment amount must be divisible by $${formatNumber(
                    sharePrice,
                  )}, the price per share.`}
                />
              )}
              {errors.temp?.investmentAmount?.type === 'maximumAmount' && (
                <ErrorMessage message={`Maximum investment is $${maxInvestmentAmount}.`} />
              )}
            </div>
          )}

          {isMaxInvestmentValid && (
            <div className="mt-8 flex w-full items-start">
              <div className="group relative inline-flex items-center">
                <input
                  type="checkbox"
                  id="item5"
                  className="mr-[12px] mt-[6px] hover:cursor-pointer"
                  checked={selected}
                  onClick={(e) => {
                    handleSelection(selected)
                  }}
                  onChange={(e) => {
                    handleSelection(selected)
                  }}
                />
                <span
                  className="-translate-x-2/2 absolute left-1/2 m-4 
                        mx-auto w-56 -translate-y-[32px] rounded-md bg-gray-800 p-1 px-1
                      text-center text-sm text-gray-100 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  Click the checkbox to accept.
                </span>
              </div>
              <div className="text-main-white block text-left text-base font-semibold sm:flow-root sm:text-2xl md:block md:text-base">
                I agree to the new amount of investment.
              </div>
            </div>
          )}

          {isMaxInvestmentValid ? (
            <>
              <div className="w-full justify-between gap-3 py-10 sm:block md:flex">
                <div className="flex flex-col sm:w-[100%] md:w-[20%]">
                  <Button btnName={'Close'} onClickHandler={() => onDismiss()} />
                </div>
                <div className="flex flex-col sm:w-[100%] md:w-auto">
                  <Button
                    btnName={'Confirm new amount of investment'}
                    btnType={'button'}
                    onClickHandler={handleSave}
                    disabled={!selected}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-full justify-center gap-3 py-10 sm:block md:flex">
                <div className="flex flex-col sm:w-[100%] md:w-auto">
                  <Button
                    btnName={'Update Annual Income and Net Worth'}
                    btnType={'button'}
                    onClickHandler={onDismiss}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

export default NonAccreditedInvestorModal
